import { CHECK_STATUS } from '../types';
import abortAfter from './abortAfter';

export const id = 'connectionQuality';
export const label = 'Connection Quality';

const SAMPLES = 5;
const PING_TIMEOUT_MS = 2000; // per-sample abort — a ping doesn't need 5s
const PING_URL = 'https://activation.upshow.tv/ping';

// Streaming-grade thresholds: buffered signage tolerates higher RTT than
// interactive apps. fetch() RTT also overestimates true network latency
// (includes TLS handshake + server processing), so we give extra headroom.
const PASS_THRESHOLD_MS = 200;
const AT_RISK_THRESHOLD_MS = 500;
const MIN_SUCCESSFUL_SAMPLES = 3;

const NOTE = 'fetch RTT includes TLS + server processing';

export async function run(ctx) {
  const start = Date.now();
  const latencies = [];

  // Warm-up: establishes TLS so measured samples reflect steady-state RTT.
  try {
    await fetch(PING_URL, { cache: 'no-store', signal: abortAfter(PING_TIMEOUT_MS) });
  } catch (_) {}

  for (let i = 0; i < SAMPLES; i++) {
    // Early-exit: if remaining samples can't reach MIN_SUCCESSFUL_SAMPLES, stop.
    const remaining = SAMPLES - i;
    if (latencies.length + remaining < MIN_SUCCESSFUL_SAMPLES) break;

    const t0 = Date.now();
    try {
      await fetch(PING_URL, {
        cache: 'no-store',
        signal: abortAfter(PING_TIMEOUT_MS),
      });
      latencies.push(Date.now() - t0);
    } catch (_err) {
      // Sample failed — discard
    }
  }

  const durationMs = Date.now() - start;
  const samples = latencies.length;

  if (samples < MIN_SUCCESSFUL_SAMPLES) {
    return {
      id,
      label,
      status: CHECK_STATUS.FAIL,
      detail: `Only ${samples} of ${SAMPLES} samples succeeded`,
      detailData: { avgLatencyMs: null, samples, note: NOTE },
      durationMs,
    };
  }

  const avgLatencyMs = Math.round(
    latencies.reduce((sum, v) => sum + v, 0) / samples
  );

  const detailData = { avgLatencyMs, samples, note: NOTE };

  let status;
  let detail;

  if (avgLatencyMs < PASS_THRESHOLD_MS) {
    status = CHECK_STATUS.PASS;
    detail = `avg ${avgLatencyMs} ms over ${samples} samples`;
  } else if (avgLatencyMs < AT_RISK_THRESHOLD_MS) {
    status = CHECK_STATUS.AT_RISK;
    detail = `avg ${avgLatencyMs} ms — elevated latency`;
  } else {
    status = CHECK_STATUS.FAIL;
    detail = `avg ${avgLatencyMs} ms — high latency`;
  }

  return {
    id,
    label,
    status,
    detail,
    detailData,
    durationMs,
  };
}
